import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../services/api'

/**
 * Hook compartido para captura de geolocalización en marcajes.
 * Lee el modo configurado por la empresa y, si toca, pide coords al navegador.
 *
 * Uso:
 *   const { modo, capturar, ultimaCaptura, cargando } = useGeoFichaje()
 *   const coords = await capturar()  // { lat, lng, precision_m } | null
 */
export function useGeoFichaje() {
  const [modo, setModo] = useState('desactivada')
  const [cargandoModo, setCargandoModo] = useState(true)
  const [ultimaCaptura, setUltimaCaptura] = useState(null)
  const cacheRef = useRef({ ts: 0, coords: null })

  useEffect(() => {
    let activo = true
    api.get('/geolocalizacion/config')
      .then(r => { if (activo) { setModo(r.data?.modo || 'desactivada'); setCargandoModo(false) } })
      .catch(() => { if (activo) setCargandoModo(false) })
    return () => { activo = false }
  }, [])

  const capturar = useCallback(() => {
    return new Promise((resolve) => {
      if (modo === 'desactivada') return resolve(null)
      if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null)

      // Cache 30s para no martillear el GPS
      const ahora = Date.now()
      if (ahora - cacheRef.current.ts < 30_000 && cacheRef.current.coords) {
        return resolve(cacheRef.current.coords)
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            precision_m: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null
          }
          cacheRef.current = { ts: Date.now(), coords }
          setUltimaCaptura(coords)
          resolve(coords)
        },
        () => resolve(null), // permisos denegados / error → null
        { enableHighAccuracy: true, timeout: 7000, maximumAge: 30_000 }
      )
    })
  }, [modo])

  return { modo, cargandoModo, capturar, ultimaCaptura }
}
